import { useEffect, useState } from 'react';
import { CurrentPage } from '../../interfaces';

export interface UseSelectedOnlyViewOptions<T> {
  /** The full set of currently-selected items, across all server pages. */
  selectedItems: T[];
  /** Rows per page, used for the client-side pagination math while toggled on. */
  pageSize: number;
  /** The page index/reset state this table already owns for its own pagination. */
  currentPageIndex: CurrentPage;
  setCurrentPageIndex: (page: CurrentPage) => void;
  /** The server-fetched page of items, shown when the toggle is off. */
  fallbackItems: T[] | undefined;
  /** The server-reported total result count, shown when the toggle is off. */
  fallbackTotalResults: number;
}

export interface UseSelectedOnlyViewResult<T> {
  showSelectedOnly: boolean;
  /** Toggles the view and resets pagination to page 1 of the new result set. */
  setShowSelectedOnly: (value: boolean) => void;
  /** Either the selected-only slice or `fallbackItems`, depending on the toggle. */
  displayedItems: T[] | undefined;
  totalResults: number;
  pagesCount: number;
}

/** Adds a "show selected only" view: filters a multi-select table to just the current selection across all pages, paginated client-side. */
export function useSelectedOnlyView<T>(options: UseSelectedOnlyViewOptions<T>): UseSelectedOnlyViewResult<T> {
  const { selectedItems, pageSize, currentPageIndex, setCurrentPageIndex, fallbackItems, fallbackTotalResults } = options;
  const [showSelectedOnly, setShowSelectedOnlyState] = useState(false);

  // Falls back to the normal view when the selection empties out while toggled on.
  useEffect(() => {
    if (selectedItems.length === 0 && showSelectedOnly) {
      setShowSelectedOnlyState(false);
    }
  }, [selectedItems.length]);

  const setShowSelectedOnly = (value: boolean) => {
    setShowSelectedOnlyState(value);
    setCurrentPageIndex({ index: 1, isPageReset: true });
  };

  const selectedPagesCount = Math.ceil(selectedItems.length / pageSize) || 1;

  // Clamps the page index down if the selection shrinks below the current page.
  useEffect(() => {
    if (showSelectedOnly && currentPageIndex.index > selectedPagesCount) {
      setCurrentPageIndex({ index: selectedPagesCount, isPageReset: false });
    }
  }, [showSelectedOnly, currentPageIndex.index, selectedPagesCount]);

  let totalResults = fallbackTotalResults;
  let pagesCount = fallbackTotalResults > 0 ? Math.ceil(fallbackTotalResults / pageSize) : 0;
  let displayedItems = fallbackItems;

  if (showSelectedOnly) {
    totalResults = selectedItems.length;
    pagesCount = selectedPagesCount;
    const effectivePageIndex = Math.min(currentPageIndex.index, pagesCount);

    const startIndex = (effectivePageIndex - 1) * pageSize;
    displayedItems = selectedItems.slice(startIndex, startIndex + pageSize);
  }

  return { showSelectedOnly, setShowSelectedOnly, displayedItems, totalResults, pagesCount };
}
